import * as Yup from 'yup'
import { format, differenceInYears } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { useMemo } from 'react'

import { EMAIL_REGEX } from '../../helpers/constants'
import Avatar from '../../Avatar'
import { getAssetLink } from '../../helpers/get-asset-link'

export const getClientsListColumns = (timezone, t) => [
  {
    Header: ' ',
    Cell: (data) => {
      const { row } = data
      const user = row.original.user
      return (
        <Avatar size="medium" style={{ marginRight: '15px', position: 'inherit' }}>
          <img
            src={getAssetLink({
              path: 'chitown-avatar/avatar',
              file: user.avatar,
              fallback: { file: 'avatar.png' },
            })}
          />
        </Avatar>
      )
    },
  },
  {
    Header: t('clients-legacy:table.columns.clientName'),
    Cell: (data) => {
      const { row } = data
      const user = row.original.user
      return <div>{user ? `${user.firstName || ''} ${user.lastName || ''}` : '-'}</div>
    },
  },
  {
    Header: t('clients-legacy:table.columns.email'),
    Cell: (data) => {
      const { row } = data
      const user = row.original.user
      return user && user.email ? user.email : '-'
    },
  },
  {
    Header: t('clients:list.columns.status'),
    Cell: (data) => {
      const { row } = data
      return `${(row.original.statusPMS && row.original.statusPMS.status) || '-'}`
    },
  },
  {
    Header: t('clients:list.columns.created'),
    Cell: (data) => {
      const user = data.row.original.user
      const createdAt = user && user.createdAt
      return createdAt ? formatInTimeZone(createdAt, timezone, 'MM/dd/yyyy h:mm a') : '-'
    },
  },
  {
    Header: t('clients-legacy:table.columns.phoneNumber'),
    Cell: (data) => {
      const { row } = data
      return row.original.phone ? formatPhone(row.original.phone) : '-'
    },
  },
  {
    Header: t('clients-legacy:table.columns.buildingName'),
    Cell: (data) => {
      const { row } = data
      const buildingId = row.original.buildingId
      return buildingId && buildingId.name ? buildingId.name : '-'
    },
  },
  {
    Header: t('clients-legacy:table.columns.unit'),
    Cell: (data) => {
      const { row } = data
      return row.original.apartment || '-'
    },
  },
]

export const getNewClientValidation = (t) => ({
  requiredString: Yup.string().required(t('clients-legacy:validation.required')),
  optionalString: Yup.string().nullable(),
  requiredEmail: Yup.string()
    .matches(EMAIL_REGEX, t('clients-legacy:validation.invalidEmail'))
    .required(t('clients-legacy:validation.required')),
  phone: Yup.string()
    .transform((v) => v || null)
    .nullable()
    .when([], {
      is: () => import.meta.env.MODE === 'production',
      then: (schema) => schema.matches(/^\d{10}$/, t('clients-legacy:validation.invalidPhone10')),
      otherwise: (schema) =>
        schema.matches(/^\d{11,14}$/, t('clients-legacy:validation.invalidPhone1114')),
    }),
  leaseStart: Yup.date().nullable(),
  leaseEnd: Yup.date().nullable(),
  moveInDate: Yup.date().nullable(),
  moveOutDate: Yup.date().nullable(),
  birth: Yup.date().nullable(),
})

export const getPropertyOptions = (options) =>
  options.map((option) => ({
    value: option,
    label: option,
  }))

export const getTimezoneTimestamp = (date, timezone) => {
  const dateStr = format(date, 'yyyy-MM-dd')
  return timezone.length > 0
    ? fromZonedTime(`${dateStr}T00:00:00`, timezone).getTime()
    : new Date(`${dateStr}T00:00:00`).getTime()
}

export const getUpdateClientBody = (values, avatar, editModeList, timezone) => {
  const {
    pets,
    buildingNumber,
    floor,
    moveInDate,
    moveOutDate,
    leaseStart,
    leaseEnd,
    birth,
    phone,
    ...updateData
  } = values

  return {
    ...updateData,
    ...(buildingNumber && { buildingNumber }),
    ...(floor && { floor }),
    ...(pets && { pets }),
    ...(avatar && !!avatar.file && editModeList.includes('avatar') && { avatar: avatar.file }),
    moveInDate: moveInDate ? getTimezoneTimestamp(moveInDate, timezone) : null,
    moveOutDate: moveOutDate ? getTimezoneTimestamp(moveOutDate, timezone) : null,
    leaseStart: leaseStart ? getTimezoneTimestamp(leaseStart, timezone) : null,
    leaseEnd: leaseEnd ? getTimezoneTimestamp(leaseEnd, timezone) : null,
    birth: birth ? getTimezoneTimestamp(birth, timezone) : null,
    phone: phone ? getPhoneBodyValue(phone) : null,
  }
}

export const formatPhone = (phone) => {
  return phone ? `+${phone}` : null
}

export const getPhoneMaxLength = () => {
  // Production: user enters 10 digits (no prefix); staging: 11-14 digits
  return import.meta.env.MODE === 'production' ? 10 : 14
}

export const getPhoneBodyValue = (phone) => {
  // User enters digits only (no "+" prefix), so send the value as-is
  return Number(phone)
}

export const getPoolName = (place) => {
  return place.name === 'Village Amenity' && place.id === 1 ? 'Old Pool' : 'New Pool'
}

export const getPoolOptions = (places) => {
  return places.map((place) => ({
    label: getPoolName(place),
    value: place.id,
  }))
}

export const useMemoizedPoolSelectOptions = (places = []) => {
  const poolSelectOptions = useMemo(() => {
    return places.map((place) => ({
      label: place.name,
      value: place.id,
    }))
  }, [places])

  return poolSelectOptions
}

export const getAgeStringTranslated = (birth, t) => {
  const age = differenceInYears(new Date(), new Date(birth))
  const className =
    !age || age >= 18 ? 'client-age-info--over-eighteen' : 'client-age-info--under-eighteen'
  const text = birth
    ? t('clients-legacy:ageInfo.yearsOld', { age })
    : t('clients-legacy:ageInfo.noAgeOnFile')

  return <span className={className}>{text}</span>
}

export const shouldDisplayPoolInfo = (selectedBuildingId) => {
  const silverLeafBuildingIds = [724, 30]

  return !!selectedBuildingId && silverLeafBuildingIds.includes(selectedBuildingId)
}
